import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { networkDuelGroupMove, networkRemoveDuel, useFetchDuelList } from 'src/network/duels';
import { useQueryClient } from '@tanstack/react-query';
import useFormsModalStore from 'src/store/forms';
import { APP_DUAL_LIST } from 'src/constants/queryKeys';
import { reorderList } from 'src/utils';

export const useDuelList = (params) => {
  const { page, projectId, selected, setSelected, filterItems } = params;

  const { setModal } = useFormsModalStore();

  const { enqueueSnackbar } = useSnackbar();

  const queryClient = useQueryClient();

  const [, setSavedPage] = useLocalStorage('savedPage');

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);

  const [deleteLoading, setDeleteLoading] = useState(false);

  const [tableData, setTableData] = useState([]);
  const [originalTableData, setOriginalTableData] = useState([]);

  const [isDraggable, setIsDraggable] = useState(false);
  const [sortLoading, setSortLoading] = useState(false);

  // ---------------------------------------------------------------------------------

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = reorderList(tableData, result.source.index, result.destination.index);
    setTableData(items);
  };

  const handleSortList = () => {
    const duelIDs = tableData?.map((item, index) => item.DuelID);
    const indexs = tableData?.map((item, index) => index).reverse();
    const newArray = [];
    for (let i = 0; i < duelIDs?.length; i++) {
      newArray.push({ DuelID: duelIDs[i], Index: indexs[i] });
    }
    setSortLoading(true);
    networkDuelGroupMove(projectId, newArray)
      .then((res) => {
        enqueueSnackbar('تفییرات با موفقیت اعمال شد');
        queryClient.invalidateQueries({ queryKey: [APP_DUAL_LIST, projectId] });
        setIsDraggable(false);
      })
      .catch((error) => console.log(error))
      .finally(() => setSortLoading(false));
  };

  const resetDragAndDrop = () => setTableData(originalTableData);

  // ---------------------------------------------------------------------------------

  const handleDeleteRow = (duelId) => {
    setDeleteLoading(true);
    networkRemoveDuel(duelId)
      .then((res) => {
        const deleteRow = tableData.filter((row) => row.DuelID !== duelId);
        setTableData(deleteRow);
        enqueueSnackbar('تورنومنت موردنظر با موفقیت حذف شد');
        const newSelectedItems = selected?.filter((id) => id !== duelId);
        setSelected(newSelectedItems);
        queryClient.invalidateQueries({ queryKey: [APP_DUAL_LIST, projectId] });
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setOpenConfirmDelete(false);
        setDeleteLoading(false);
      });
  };

  const handleDeleteRows = async (duelIDs) => {
    try {
      setDeleteLoading(true);
      const deletePromises = duelIDs.map((id) => networkRemoveDuel(id));
      await Promise.all(deletePromises);
      const newTableData = tableData.filter((row) => !duelIDs.includes(row.DuelID));
      setTableData(newTableData);
      enqueueSnackbar('تورنومنت های  موردنظر با موفقیت حذف شدند');
      await queryClient.invalidateQueries({ queryKey: [APP_DUAL_LIST, projectId] });
      setSelected([]);
    } catch (error) {
      console.log(error);
    } finally {
      setOpenConfirmDelete(false);
      setDeleteLoading(false);
    }
  };

  const handleEditRow = (duelInfo) => setModal({ data: duelInfo, isOpen: true, type: APP_DUAL_LIST, title: 'ویرایش تورنومنت' });

  const { isLoading, isFetching, data } = useFetchDuelList(page, 50, filterItems);

  useEffect(() => {
    if (data) {
      const duelList = data?.data?.items || [];
      setTableData([...duelList]);
      setOriginalTableData([...duelList]);
      setSavedPage({
        pageIndex: page,
        pageName: 'duels',
      });
    }
  }, [data, page]);

  return {
    tableData,
    isLoading: isLoading || isFetching,
    isFetching,
    deleteLoading,
    handleDeleteRow,
    handleDeleteRows,
    handleEditRow,
    openConfirmDelete,
    setOpenConfirmDelete,
    isDraggable,
    sortLoading,
    setIsDraggable,
    onDragEnd,
    handleSortList,
    resetDragAndDrop,
  };
};
